import React, { useEffect, useRef } from 'react';
import { Card, Button, Typography, theme, Spin, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';

import RateForm from './rateForm';
import {
    rateDetailsGetRequest,
    rateDetailsUpdateRequest,
} from '@/AppRedux/actions/admin/rateDetailConfig';
import { RateData } from '@/AppRedux/actions/admin/rateDetailConfig/rateDetailConfigType';
import { useAppDispatch, useAppSelector } from '@/AppRedux/store/hooks';


const { Title, Text } = Typography;

const EditRate: React.FC = () => {
    const navigate = useNavigate();
    const { rateCode } = useParams<{ rateCode: string }>();
    const { token } = theme.useToken();
    const dispatch = useAppDispatch();

    const { rateData, loading, process } = useAppSelector((state) => state.rateDetailConfig);

    const normalizedRateCode = rateCode ? decodeURIComponent(rateCode).toUpperCase() : '';
    const saving = process.action === 'RATE_DETAILS_UPDATE_REQUEST' && loading;

    const useCurrentFirstRender = useRef(true)

    useEffect(() => {
        console.log('EditRate useEffect triggered with rateCode:', rateCode, 'normalizedRateCode:', normalizedRateCode);
        if (normalizedRateCode) {
            if (useCurrentFirstRender.current) {
                dispatch(rateDetailsGetRequest(normalizedRateCode));
                useCurrentFirstRender.current = false;
            }
        } else {
            message.error('Invalid rate code.');
            setTimeout(() => {
                navigate(-1);
            }, 300);
        }
    }, [dispatch, navigate, normalizedRateCode, useCurrentFirstRender]);

    useEffect(() => {
        if (process.action === 'RATE_DETAILS_UPDATE_REQUEST') {
            if (process.status && process.message) {
                message.success(process.message);
                setTimeout(() => {
                    navigate(-1);
                }, 300);

            } else if (!process.status && process.message) {
                message.error(process.message);
            }
        }
    }, [process, navigate]);


    const handleSubmit = async (values: RateData) => {
        dispatch(rateDetailsUpdateRequest(values));
    };

    const handleCancel = () => {
        navigate(-1);
    };

    if (loading && !rateData) {
        return (
            <div style={{ padding: 24, background: token.colorBgLayout, minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }} >
                <Spin size="large" description="Loading rate data..." />
            </div>
        );
    }

    if (!rateData) {
        return <div style={{ padding: 24 }}>Rate not found.</div>;
    }

    const headerDisplayName = rateData.contents?.find(c => c.lang === 'en')?.display_name || rateData.contents?.[0]?.display_name || '...';

    return (
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
            <Card>
                {/* Header */}
                <div style={{ marginBottom: 32 }}>
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={handleCancel}
                        style={{ marginBottom: 16 }}
                        disabled={saving}
                    >
                        Back to Rate List
                    </Button>
                    <Title level={2} style={{ margin: 0 }}>
                        Edit Rate
                    </Title>
                    <Text type="secondary">
                        Update rate information for {headerDisplayName}
                    </Text>
                </div>

                {/* Form */}
                <Spin spinning={saving} tip="Saving data...">
                    <RateForm
                        mode="edit"
                        initialData={rateData}
                        onSubmit={handleSubmit}
                        onCancel={handleCancel}
                    />
                </Spin>
            </Card>
        </div>
    );
};

export default EditRate;
